import React from 'react';
import { Platform, RefreshControl, ScrollView, View, type ViewStyle } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { IconButton } from './Button';
import { ShellContext, useShell } from './ShellContext';
import { Text } from './Text';
import { useTheme } from './theme';

export interface ScreenProps {
  children: React.ReactNode;
  /** wrap in a ScrollView (default true). Use false for screens with their own FlatList/chat layout. */
  scroll?: boolean;
  /** pull-to-refresh (native) */
  refreshing?: boolean;
  onRefresh?: () => void;
  /** max content width on large screens (default 1200) */
  maxWidth?: number;
  padded?: boolean;
  contentStyle?: ViewStyle;
  testID?: string;
}

/**
 * Standard screen container: safe areas, themed background, scroll + pull-to-refresh on native,
 * centred max-width column on web/tablets.
 */
export function Screen({ children, scroll = true, refreshing = false, onRefresh, maxWidth = 1200, padded = true, contentStyle, testID }: ScreenProps) {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isWide, tabRoots } = useShell();
  const route = useRoute();
  const inTabs = tabRoots.has(route.name);
  const pad = padded ? (isWide ? 24 : 16) : 0;
  const inner: ViewStyle = { width: '100%', maxWidth, alignSelf: 'center', padding: pad, gap: 16 };
  const top = isWide ? 0 : insets.top;
  const bottom = isWide || inTabs ? 0 : insets.bottom;
  if (!scroll)
    return (
      <View testID={testID} style={{ flex: 1, backgroundColor: colors.bg, paddingTop: top, paddingBottom: bottom }}>
        <View style={[inner, { flex: 1 }, contentStyle]}>{children}</View>
      </View>
    );
  return (
    <View testID={testID} style={{ flex: 1, backgroundColor: colors.bg, paddingTop: top }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: bottom + 24 }}
        keyboardShouldPersistTaps="handled"
        refreshControl={onRefresh && Platform.OS !== 'web' ? <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} /> : undefined}
      >
        <View style={[inner, contentStyle]}>{children}</View>
      </ScrollView>
    </View>
  );
}

/**
 * Page title row. Shows a back button on non-root screens and, on phones, the global
 * search (command palette) + notifications buttons on tab-root screens.
 */
export function PageHeader({ title, subtitle, actions, back }: { title: string; subtitle?: string; actions?: React.ReactNode; back?: boolean }) {
  const nav = useNavigation();
  const route = useRoute();
  const shell = React.useContext(ShellContext);
  const { colors } = useTheme();
  const isRoot = shell.tabRoots.has(route.name) || route.name === 'Home';
  const showBack = back ?? (!isRoot && !(shell.isWide && shell.topLevel.has(route.name)));
  const goBack = () => (nav.canGoBack() ? nav.goBack() : shell.go('Home'));
  return (
    <View style={{ gap: 10 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        {showBack && <IconButton icon="arrowLeft" label="Back" onPress={goBack} testID="header-back" />}
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text variant={shell.isWide ? 'h1' : 'h2'} numberOfLines={2} accessibilityRole="header">
            {title}
          </Text>
          {!!subtitle && (
            <Text variant="muted" numberOfLines={2}>
              {subtitle}
            </Text>
          )}
        </View>
        {!shell.isWide && isRoot && (
          <View style={{ flexDirection: 'row' }}>
            <IconButton icon="search" label="Search screens" onPress={shell.openPalette} testID="header-search" />
            <IconButton icon="bell" label="Notifications" badge={shell.unread} onPress={shell.openNotifications} />
          </View>
        )}
      </View>
      {actions ? <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, borderColor: colors.border }}>{actions}</View> : null}
    </View>
  );
}
